/**
 * 過去の日の結果をさかのぼって見る（課題 #156。2026-10-02 高尾さん依頼）。
 *
 * - 振り返り履歴の「日ごとの振り返り」の 1 行: 日付・録音件数と、ダッシュボードの
 *   「今日のコンディション傾向」と同じ 5 項目（活力・ストレス傾向・集中・思考モード・反応の強さ）
 * - 日付を押すと、その日の結果（/dashboard/history/:date）を開く
 *
 * データは日次レポートの履歴（本人の分だけ返る）。日次レポートの中身（content_json）に、
 * その日の指標・前日比・AI コメント・録音件数が入っている。
 */
import type { DailyReportDomains, DailyReportHistoryItem } from '../api/client';

export interface DayHistoryRow {
  date: string;
  /** その日の録音件数（日次レポートに記録された件数）。無ければ null */
  voiceCount: number | null;
  /** 5 項目。旧い形式の日（指標が無い）は null */
  domains: DailyReportDomains | null;
}

function isDomains(v: unknown): v is DailyReportDomains {
  if (!v || typeof v !== 'object') return false;
  const d = v as Record<string, unknown>;
  return ['vitality', 'tension', 'cognition', 'emotion_stability'].every((k) => typeof d[k] === 'number');
}

/** 履歴の 1 件から、一覧の 1 行を作る */
export function dayHistoryRow(item: DailyReportHistoryItem): DayHistoryRow {
  const c = (item.content ?? {}) as Record<string, unknown>;
  return {
    date: item.report_date,
    voiceCount: typeof c.voice_count === 'number' ? c.voice_count : null,
    domains: isDomains(c.domains) ? c.domains : null,
  };
}

/** 'YYYY-MM-DD' として読めるか（URL の日付を確かめる） */
export function isValidDate(ymd: string | undefined | null): ymd is string {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return false;
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/**
 * その日の録音を取る範囲（端末の時刻で 0:00〜23:59:59.999）。ダッシュボードの「今日の録音」と同じ考え方。
 * 録音の一覧の API（GET /v1/voice-records）の from / to は、録音日時で絞る
 */
export function localDayRange(ymd: string): { from: string; to: string } {
  const [y, m, d] = ymd.split('-').map(Number);
  return {
    from: new Date(y, m - 1, d, 0, 0, 0, 0).toISOString(),
    to: new Date(y, m - 1, d, 23, 59, 59, 999).toISOString(),
  };
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

/** 「2026/10/01（木）」の形 */
export function formatDayLabel(ymd: string): string {
  if (!isValidDate(ymd)) return ymd;
  const [y, m, d] = ymd.split('-').map(Number);
  const w = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${y}/${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}（${w}）`;
}
